<template lang="pug">
	.reconciliation-dashboard
		.flex-col.space
			.flex-row.justify.wrap.nav-header-wrapper
				.flex-col.nav-header
					.flex-row.space
						span(v-if="mode === 'campaign'") {{$tCaption('Radio campaigns')}}
						span(v-if="mode === 'station'") {{$tCaption('Station logs')}}
						slot(name="header")
			.flex-row.area.space-large.wrap.align-center.justify
				.flex-row.area.space-large.wrap.align-center
					template(v-if="mode === 'campaign'")
						.flex-col.center
							span.upper.font-small.grey {{$tCaption('Campaign')}}
							Dropdown.selector(v-if="",
								:getTotal="(data) => null", :getItems="(data) => data.Results", :getData="$Data.getReconciliationCampaigns",
								:columns="[{ dataField: 'Name', caption: $tCaption('Campaign') }]",
								displayExpr="Name",
								valueExpr="Oid",
								:placeholder="$tCaption('Select campaign')",
								:selectedItems="campaignId",
								:showColumnHeaders="false",
								:filtersVisible="false",
								:searchVisible="true",
								:multiple="false",
								:minSearchLength="0",
								@select-data="v => selectedCampaign = v")
						.flex-col.center
							span.upper.font-small.grey {{$tCaption('Period from')}}
							DxDateBox(:value="period.from", @value-changed="v => period.from = v.value", :show-clear-button="true", :width="150")
						.flex-col.center
							span.upper.font-small.grey {{$tCaption('Period to')}}
							DxDateBox(:value="period.to", @value-changed="v => period.to = v.value", :show-clear-button="true", :width="150")
					template(v-if="mode === 'station'")
						.flex-col.center
							span.upper.font-small.grey {{$tCaption('Station')}}
							Dropdown.selector(v-if="",
								:getTotal="r => r.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="(a,b,c) => $Data.getExternalStations(a,b,{...c, allCountries: true, onlyLicensed: true })",
								:columns="[{ dataField: 'Name', caption: $tCaption('Station') }]",
								displayExpr="Name",
								valueExpr="Oid",
								:placeholder="$tCaption('Select station')",
								:selectedItems="stationId",
								:showColumnHeaders="false",
								:filtersVisible="false",
								:searchVisible="true",
								:multiple="false",
								:minSearchLength="0",
								@select-data="v => stationId = v && v.Oid || null")
						.flex-col.center
							span.upper.font-small.grey {{$tCaption('Log date')}}
							DxDateBox(:value="period.from", @value-changed="v => { period.from = v.value; period.to = v.value; }", :show-clear-button="true", :width="150")
					DxButton(v-if="filtersSpecified", icon="refresh", @click="loadData", :disabled="isLoading")
					slot(name="filters")
				.flex-col.center(v-if="mode === 'station'")
					span.upper.font-small.grey {{$tCaption('Play log at')}}
					.flex-row.space.align-center
						DxButton.small(:id="'stationPlayAt'", icon="fas fa-play", :disabled="!playAtDate || !filtersSpecified", @click="onPlayAtClick")
						DxDateBox(:value="playAtDate", :disabled="!filtersSpecified", @value-changed="v => { playAtDate = v.value; }", :show-clear-button="true", :width="230", type="datetime", pickerType="rollers", :displayFormat="(v) => $moment(v).format('lll')")
			.flex-row(v-if="filtersSpecified")
				.flex-col.justify
					.loading.flex-row.space(v-if="isLoading")
						.loader
							i.fas.fa-circle-notch.fa-spin
						span {{$tCaption('Loading') + '..'}}
					.dashboard-items(v-else)
						.products-data
							RadioTab(ref="radioTab", :data="radioData", :mode="mode", :stationId="stationId")
</template>

<script>
	import VueTypes from 'vue-types';
	import _ from 'lodash';
	import DxDateBox from 'devextreme-vue/date-box';
	import DxButton from 'devextreme-vue/button';
	import moment from 'moment';
	import Data from 'app-core/Data';
	import RadioTab from './RadioTab.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import Promise from 'bluebird';

	export default {
		translationCategory: 'reconciliation.Dashboard',
		name: 'dashboard',
		components: {
			DxDateBox,
			DxButton,
			Dropdown,
			RadioTab
		},
		props: {
			mode: VueTypes.oneOf(['campaign', 'station']).def('campaign')
		},
		data() {
			return {
				selectedCampaign: null,
				stationId: null,
				period: {
					from: null,
					to: null
				},
				isLoading: false,
				radioData: {
					CampaignSpots: [],
					LastPlayedSpot: {},
					Schedule: [],
					SpotsAired: 0,
					SpotsNotAired: 0,
					LastLoggedDate: null,
					LastAudioLoggingDate: null
				},
				playedStatuses: [
					() => this.$tCaption('PreScheduled'),
					() => this.$tCaption('Scheduled'),
					() => this.$tCaption('Aired'),
					() => this.$tCaption('Missed'),
					() => this.$tCaption('FingerPrinted'),
					() => this.$tCaption('None'),
				],
				playAtDate: null
			};
		},
		computed: {
			filtersSpecified() {
				return (this.mode === 'campaign' && !!this.campaignId || this.mode === 'station' && !!this.stationId) && !!this.period.from && !!this.period.to;
			},
			campaignId() {
				return this.selectedCampaign && this.selectedCampaign.Oid || null;
			}
		},
		watch: {
			'period.from'() {
				this.loadData();
			},
			'period.to'() {
				this.loadData();
			},
			'campaignId'() {
				this.loadData();
			},
			'stationId'() {
				this.loadData();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.reconciliationDashboard = this;
			this.period.from = moment().toDate();
			if (this.mode === 'campaign') {
				this.period.from = moment().startOf('month').toDate();
				this.period.to = moment().endOf('month').toDate();
			} else {
				this.period.from = this.period.to = moment().add('days', -1).toDate();
			}
		},
		methods: {
			onPlayAtClick() {
				if (this.$refs.radioTab)
					this.$refs.radioTab.playStationLog(this.stationId, this.playAtDate, '#stationPlayAt');
			},
			loadData() {
				this.$emit('load-data');
				if (!this.filtersSpecified) return;
				var params = {
					adthosChannelId: this.mode === 'station' && this.stationId || null,
					startDate: this.period.from && moment(this.period.from).startOf('day').utcOffset(0).format('YYYY-MM-DDTHH:mm:ss') || null,
					endDate: this.period.to && moment(this.period.to).endOf('day').utcOffset(0).format('YYYY-MM-DDTHH:mm:ss') || null,
					adthosCampaignId: this.mode === 'campaign' && this.selectedCampaign && this.selectedCampaign.Type === 0 && this.campaignId || null,
					campaignId: this.mode === 'campaign' && this.selectedCampaign && this.selectedCampaign.Type === 1 && this.campaignId || null
				};
				var paramsString = [];
				for(var param in params) {
					if (params[param] !== null)
						paramsString.push(`${param}=${params[param]}`);
				}

				var getSpot = (f) => ({
					Station: f.AdthosChannelId && f.AdthosChannelId.Name,
					StationId: f.AdthosChannelId && f.AdthosChannelId.Oid,
					Title: f.SpotTitle,
					ISCI: f.Isci,
					Material: f.MaterialId,
					PlayTime: f.PlayTime,
					PlayedDateTime: f.PlayTime,
					Duration: f.SpotLength,
					Status: this.playedStatuses[f.PlayedStatus],
					ScheduleId: f.ScheduleId,
					PlayedStatus: f.PlayedStatus,
					CntImpressions: f.CntImpressions,
					PositionSold: f.PositionSold
				});

				this.isLoading = true;
				if (this.mode === 'station') {
					this.$Data.getExternalApi('Station/GetLastBlobInLogsContainer', `stationId=${this.stationId}`, 1).then(r => {
						if (r && r.Name) {
							this.radioData.LastLoggedDate = r.LastModified || r.Created;
						}
					});
					this.$Data.getExternalApi('Station/GetLastBlobInLoggingContainer', `stationId=${this.stationId}`, 1).then(r => {
						if (r && r.Name) {
							this.radioData.LastAudioLoggingDate = r.LastModified || r.Created;
						}
					});
				}
				Promise.all([
					this.$Data.getExternalApi('Reconciliation/GetInsertions', paramsString.join('&'), 10000).then(r => {
						if (this._isDestroyed) return;
						this.radioData.Schedule = r.Results.map(f => getSpot(f));
					}),
					this.$Data.getExternalApi('Reconciliation/GetInsertions', `${paramsString.filter(f => f.indexOf('Date') === -1).join('&')}&selectOnlyPlayed=true`, 1).then(r => {
						if (this._isDestroyed) return;
						this.radioData.LastPlayedSpot = r.Results.map(f => getSpot(f))[0] || {};
					}),
					this.$Data.getExternalApi('Reconciliation/GetInsertionsSummary', paramsString.join('&'), 10000).then(r => {
						if (this._isDestroyed) return;
						this.radioData.SpotsAired = _.sumBy(r, v => v.AiredSpotsCount || 0);
						this.radioData.SpotsNotAired = _.sumBy(r, v => v.ScheduledSpotsCount || 0);
						this.radioData.CampaignSpots = r.map(f => ({
							...f,
							ScheduledSpotsCount: (f.ScheduledSpotsCount || 0) + (f.AiredSpotsCount || 0)
						}));
					})
				]).finally(() => {
					this.isLoading = false;
				});
			}
		}
	};
</script>
<style lang='scss'>
	.reconciliation-dashboard {
		@import "main/themes/generated/variables.base.scss";

		padding: 10px;

		.campaign-data {
			justify-content: center;

			.campaign-info {
				border-radius: 4px;
				background: rgba(0, 0, 0, 0.02);
				min-width: 20vw;
				justify-content: space-around;

				> .block {
					padding: 10px;
					justify-content: center;
					color: $base-accent;

					&:not(:last-child) {
						border-right: 1px solid rgba(0,0,0,0.03);
					}
				}
			}
		}

		.totals {
			align-items: center;
			font-size: 24px;
			font-weight: 100;

			> * {
				padding: 10px;
			}
		}

		.text-overflow {
			overflow: hidden;
			text-overflow: ellipsis;
			width: 140px;
			white-space: nowrap;
			line-height: 32px;
		}

		.hidden {
			visibility: collapse;
		}

		.header-label {
			font-size: 12px;
			color: #c1c1c1;
			text-transform: uppercase;
		}

		.area {
			padding: 10px;
			background: #fff;
		}

		.compact {
			tr.dx-row > td:not(.no-padding) {
				padding: 8px !important;
			}

			tr.dx-row > td.no-padding {
				padding: 0px !important;
			}

			&.dx-button {
				margin-top: -4px;
			}
		}

		.impressions-map-holder {
			position: relative;
		}

		.products-total-grid {
			position: absolute;
			top: 10px;
			left: 50px;
			z-index: 1000;
			opacity: 0.9;
		}

		.dx-item.dx-tab.dx-tab-selected {
			background: #e6e6e6 !important;
		}

		.dx-tagbox-single-line .dx-tag-container.dx-texteditor-input-container {
			overflow: initial !important;
		}

		.dx-datebox {
			height: 36px;
		}

		.number-data {
			padding: 6px 0px 0px 0px;
			font-size: 24px;
			font-weight: 100;
		}

		.number-widget {
			margin-left: 20px !important;
		}

		.zips-tag-box {
			margin-top: 10px;
		}
	}
</style>
